<script setup lang="ts">
// Asks once per device whether to notify while hrth is closed (HRTH-200), rather than leaving
// it to the Settings toggle nobody finds. A card, not a straight permission prompt: browsers
// only allow that from a tap. Not now says where the toggle is, then goes.
import { computed, onBeforeUnmount, ref } from 'vue'
import { needsInstallForPush } from '../lib/push'
import { useSession } from '../stores/session'
import { useUnread } from '../stores/unread'

const session = useSession()
const unread = useUnread()
const installFirst = needsInstallForPush()
/** Showing where to turn them on later. */
const later = ref(false)
let laterTimer: ReturnType<typeof setTimeout> | null = null
const asking = computed(
  () => unread.notificationsUnasked && !unread.notifications && !!session.features.push_key && !installFirst,
)

function showLater(): void {
  later.value = true
  laterTimer = setTimeout(() => (later.value = false), 8_000)
}

async function turnOn(): Promise<void> {
  await unread.enableNotifications(true)
  // Turned down at the OS or browser prompt.
  if (!unread.notifications) showLater()
}

async function notNow(): Promise<void> {
  await unread.declineNotifications()
  showLater()
}

onBeforeUnmount(() => {
  if (laterTimer) clearTimeout(laterTimer)
})
</script>

<template>
  <div
    v-if="asking || later"
    class="panel fixed right-4 bottom-4 left-4 z-40 mx-auto max-w-sm p-4 text-sm sm:left-auto"
    role="dialog"
    aria-label="Notifications"
    data-testid="notifications-prompt"
  >
    <template v-if="later">
      <p data-testid="notifications-prompt-later">
        You can turn them on any time in Settings › Linked devices › Device Notifications.
      </p>
      <button class="btn-ghost mt-3" @click="later = false">OK</button>
    </template>
    <template v-else>
      <p>Get notified about messages and calls, even when hrth is closed?</p>
      <div class="mt-3 flex justify-end gap-2">
        <button class="btn-ghost" data-testid="notifications-prompt-later-button" @click="notNow">Not now</button>
        <button class="btn-primary" data-testid="notifications-prompt-on" @click="turnOn">Turn on</button>
      </div>
    </template>
  </div>
</template>
